import React from 'react';
import { IconScissors, IconRotate } from '@tabler/icons-react';
import MemoryCard, { PlaceholderCard } from './MemoryCard';
import SavedTicketBanner from './SavedTicketBanner';
import styles from './MemoryBoard.module.scss';

const MemoryBoard = ({
    cards,
    onFlipCard,
    matchedPairsCount,
    totalPairsCount,
    attempts,
    hasTicket,
    goldenFact,
    onRedeemTicket,
    announcement,
    placeholderIconName,
    placeholderIconSize,
    placeholderIconColor,
    cardBackIconName,
    cardBackIconSize,
    cardIconSize,
    cardColorType,
    cardCustomColor,
}) => {
    const isEightCardLayout = cards.length === 8;
    const isSixCardLayout = cards.length === 6;
    const useThreeColumns = isEightCardLayout || isSixCardLayout;

    const resolvedThemeColor =
        cardColorType === 'secondary'
            ? 'var(--bs-secondary, #6c757d)'
            : cardColorType === 'custom' && cardCustomColor
              ? cardCustomColor
              : 'var(--bs-primary, #0067C7)';

    return (
        <section className={`${styles.boardSection} my-3`} aria-label="Spielfeld">
            {/* Screen Reader Live Region for WCAG 2.1 AA Compliance */}
            <div className="visually-hidden" aria-live="polite" aria-atomic="true">
                {announcement}
            </div>

            {/* Cards Grid Container */}
            <div className={useThreeColumns ? styles.cardsGrid3Cols : styles.cardsGrid}>
                {isEightCardLayout ? (
                    <>
                        {cards.slice(0, 4).map((card, idx) => (
                            <MemoryCard
                                key={card.id}
                                card={card}
                                index={idx}
                                onFlip={onFlipCard}
                                backIconName={cardBackIconName}
                                frontIconSize={cardIconSize}
                                backIconSize={cardBackIconSize}
                                cardColorType={cardColorType}
                                cardCustomColor={cardCustomColor}
                                shouldPulse={idx === 0 && attempts === 0 && !cards.some((c) => c.isFlipped)}
                            />
                        ))}
                        <PlaceholderCard
                            key="placeholder"
                            iconName={placeholderIconName}
                            iconSize={placeholderIconSize}
                            iconColor={placeholderIconColor || resolvedThemeColor}
                        />
                        {cards.slice(4).map((card, idx) => (
                            <MemoryCard
                                key={card.id}
                                card={card}
                                index={idx + 4}
                                onFlip={onFlipCard}
                                backIconName={cardBackIconName}
                                frontIconSize={cardIconSize}
                                backIconSize={cardBackIconSize}
                                cardColorType={cardColorType}
                                cardCustomColor={cardCustomColor}
                                shouldPulse={false}
                            />
                        ))}
                    </>
                ) : (
                    cards.map((card, idx) => (
                        <MemoryCard
                            key={card.id}
                            card={card}
                            index={idx}
                            onFlip={onFlipCard}
                            backIconName={cardBackIconName}
                            frontIconSize={cardIconSize}
                            backIconSize={cardBackIconSize}
                            cardColorType={cardColorType}
                            cardCustomColor={cardCustomColor}
                            shouldPulse={idx === 0 && attempts === 0 && !cards.some((c) => c.isFlipped)}
                        />
                    ))
                )}
            </div>

            {/* Stats Block */}
            <div className={`${styles.statsBlock} d-flex flex-column py-2 gap-1 mb-2 w-100 text-dark`}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <IconScissors size={20} stroke={resolvedThemeColor} strokeWidth={2} />
                        <span style={{ fontSize: '15px' }}>Gefundene Paare</span>
                    </div>
                    <span style={{ fontSize: '15px', fontWeight: 600 }}>
                        {matchedPairsCount}/{totalPairsCount}
                    </span>
                </div>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <IconRotate size={20} stroke={resolvedThemeColor} strokeWidth={2} />
                        <span style={{ fontSize: '15px' }}>Versuche</span>
                    </div>
                    <span style={{ fontSize: '15px', fontWeight: 600 }}>{String(attempts).padStart(3, '0')}</span>
                </div>
            </div>

            {/* Saved Ticket Banner (if player saved golden ticket) */}
            {hasTicket && (
                <div className={styles.bannerWrapper}>
                    <SavedTicketBanner goldenFact={goldenFact} onRedeem={onRedeemTicket} />
                </div>
            )}
        </section>
    );
};

export default MemoryBoard;
